<template>
  <section>
    <h2 class="mb-0">{{ $t('designSelect.title') }}</h2>
    <!-- Design Category Slider -->
    <div class="mt-3">
      <b-row ref="design-category-panel" class="design-category-row text-center flex-nowrap">
        <b-button
          v-for="(designCategory, key) in designCategories"
          :key="key"
          :ref="key === selectedDesignCategory ? `selected-design-category` : ''"
          class="design-category-col col"
          variant="outline"
          :class="key === selectedDesignCategory ? 'design-category-selected' : ''"
          @click="setDesignCategory(key)"
        >
          <div>{{ designCategory }}</div>
        </b-button>
      </b-row>
    </div>
    <div ref="design-selector-panel" class="pl-3 design-selector-panel">
      <b-row v-for="(splitOption, i) in splitOptions" :key="`design-selector-row-${i}`" class="my-3 flex-nowrap">
        <b-col
          v-for="option in splitOption"
          :key="option.title"
          :ref="option.title === design ? `selected-design` : `${option.title}`"
          cols="3"
          sm="2"
          md="2"
          xl="2"
          class="p-1 design-selector-col"
        >
          <b-button
            :key="`${option.title}`"
            :title="option.title"
            class="design-option text-center p-0"
            :class="{ selected: option.title === design }"
            :aria-label="`Choose ${option.title}`"
            @focus="setFocus(option.title)"
            @focusout="unsetFocus"
            @click="setDesign(option.title)"
          >
            <custom-design
              :base-tag-svg-img-path="baseTagSvgImgPath ? getImage(baseTagSvgImgPath) : false"
              :design-svg-img-path="option.image ? getImage(option.image) : false"
              :boundary-padding="5"
              :scale="0.85"
              :height="95"
              :width="95"
            />
          </b-button>
        </b-col>
      </b-row>
    </div>
    <input id="product-style-design" v-model="design" type="text" class="d-none" hidden />
  </section>
</template>

<script>
import getImage from '@/mixins/getImage';
import CustomDesign from './CustomDesign';
import { KeyboardNavigationMixins } from '@/mixins/keyboardNavigation';

export default {
  name: 'DesignSelect',
  components: { CustomDesign },
  mixins: [getImage, KeyboardNavigationMixins],
  props: {
    value: {
      type: String,
      required: false,
      default: '',
    },
    options: {
      type: Array,
      required: true,
    },
    baseTagSvgImgPath: {
      type: String,
      default: null,
      required: false,
    },
  },
  data() {
    return {
      design: this.value,
      selectedDesignCategory: 'DEFAULT',
      designCategories: {
        DEFAULT: 'All',
        F: 'Favorites',
        V: 'Vignettes',
        S: 'Sports',
        N: 'Nature',
        A: 'Animals',
        Z: 'Zodiac',
      },
      memoizeCategories: {},
      windowWidth: window.innerWidth,
    };
  },
  computed: {
    splitOptions() {
      if (this.memoizeCategories[this.selectedDesignCategory]) return this.memoizeCategories[this.selectedDesignCategory];
      const rawOptions = [
        ...this.options.filter((option) => this.selectedDesignCategory === 'DEFAULT' || option.category === this.selectedDesignCategory),
      ];
      const chunkOfOptions = [[], []];
      for (let i = 0; i < rawOptions.length; i++) {
        i % 2 === 0 ? chunkOfOptions[0].push(rawOptions[i]) : chunkOfOptions[1].push(rawOptions[i]);
      }
      // Memoize Categories
      // eslint-disable-next-line vue/no-side-effects-in-computed-properties
      this.memoizeCategories[this.selectedDesignCategory] = chunkOfOptions;
      return chunkOfOptions;
    },
    navigableOptions() {
      return this.splitOptions;
    },
  },
  mounted() {
    if (this.value || this.design) this.scrollToSelected();
    window.onresize = () => {
      this.windowWidth = window.innerWidth;
    };
  },
  created() {
    if (!this.value) this.setDesign(this.options[0].title);
  },
  updated() {
    this.$nextTick(() => {
      if (!this.value && this.options?.[0]?.title) this.setDesign(this.options[0].title);
    });
  },
  methods: {
    setDesign(design) {
      if (design === this.design) {
        return;
      } else {
        this.design = design;
      }
      this.$emit('input', this.design);
      this.$emit('change');
    },
    setDesignCategory(designCategoryKey) {
      this.$nextTick(() => {
        this.selectedDesignCategory = designCategoryKey;
      });
    },
    scrollToSelected(design) {
      this.$nextTick(() => {
        this.$refs?.['design-selector-panel']?.scroll({
          top: 0,
          left: this.$refs[design || 'selected-design']?.[0]?.offsetLeft - 40 || 0,
          behavior: 'smooth',
        });
      });
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@docupet/component-library/src/assets/styles/_variables';
.design-category-row {
  overflow-x: auto;
  scroll-snap-type: both proximity;
  overflow-y: hidden;
  white-space: nowrap;
  & .design-category-col {
    color: $dark;
    cursor: pointer;
    padding-top: 0.5rem;
    padding-bottom: 0.5rem;
    &.design-category-selected {
      color: $secondary;
      border-bottom: 4px solid $secondary;
    }
  }
  &::-webkit-scrollbar {
    background-color: $dp-gray-2;
    height: 0.25rem !important;
  }
  &::-webkit-scrollbar-thumb {
    background-color: $secondary;
    border-radius: 1px;
  }
}

.design-selector-panel {
  scroll-snap-type: both proximity;
  overflow-x: scroll;
  overflow-y: hidden;
  &::-webkit-scrollbar {
    background-color: $dp-gray-2;
    height: 0.75rem !important;
  }
  &::-webkit-scrollbar-thumb {
    background-color: $secondary;
    border-radius: 10px;
  }
}

.design-selector-col {
  min-width: 100px;
  &:last-child {
    margin-right: 0;
  }
}

.design-option {
  border-radius: 10px;
  background: transparent;
  height: 100px;
  width: 100px;
  border: 0;
  &.selected {
    border: 2px solid $dp-blue;
    background-color: rgba(204, 236, 249, 0.5);
  }
  &:hover,
  &:active,
  &:focus {
    &:not(.disabled) {
      border: 2px solid $dp-blue;
      box-shadow: 0 0 0 0.2rem $dp-light-blue;
      background-color: rgba(204, 236, 249, 0.5) !important;
    }
  }
}
</style>
